import { useDeleteEcoActivity, useEcoActivityList, useGetFacts } from "../../../queries/facts";
import 'react-toastify/dist/ReactToastify.css';
import { toast, ToastContainer } from "react-toastify";
import { useGetEventList } from '../../../queries/event';
import TanStackTable from '../../../components/TanStackTable';
import { createColumnHelper } from '@tanstack/react-table';
import Chip from '../../../components/Chip';
import { useNavigate } from 'react-router-dom';
import { FaTrash } from 'react-icons/fa';
import { useMemo, useState, useEffect } from 'react';
import { ActionButton } from '../../../components/Forms/ActionButton';
import { useGetAllUsers } from '../../../queries/user';
const EditIcon = require("../../../utils/media/icons/view_icon.png");

const EcoActivityList = () => {

    const navigate = useNavigate();
    const { data, isPending } = useEcoActivityList();
    const { data: users, isPending: isUserPending } = useGetAllUsers(toast);
    const { mutateAsync, isPending: isDeleting } = useDeleteEcoActivity();
    const columnHelper = createColumnHelper();
    
    const [filteredData, setFilteredData] = useState([]);
    const [filters, setFilters] = useState({
        title: '',
        userId: '',
        status: '',
        homepage: '',
    });

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        let filtered = Array.isArray(data) ? data : [];

        if (filters.userId) {
            filtered = filtered.filter((activity) =>
                activity.userId === filters.userId
            );
        }
        
        if (filters.title) {
            filtered = filtered.filter((activity) => 
                activity.title.toLowerCase().includes(filters.title.toLowerCase())
            );
        }
        
        if (filters.status) {
            filtered = filtered.filter((activity) => 
                filters.status === 'active' ? activity.active === true : activity.active === false
            );
        }
        
        if (filters.homepage) {
            filtered = filtered.filter((activity) => 
                filters.homepage === 'active' ? activity.showInHomePage === true : activity.showInHomePage === false
            );
        }
        
        setFilteredData(filtered);
    };

    const handleDelete = async (id) => {
        try {
            const confirm = window.confirm("Are you sure you want to delete this Eco Activity?");
            if(confirm) await mutateAsync(id);
        } catch (error) {
            toast.error("Error deleting Eco Activity");
        }
    }
    
    useEffect(() => {
        if (Array.isArray(data)) {
            setFilteredData(data);
        } else {
            setFilteredData([]);
        }
    }, [data]);

    // Create columns definition at the top level
    const columns = useMemo(() => [
        columnHelper.display({
            id: 'srno',
            header: 'Sr No.',
            cell: info => info.row.index + 1,
        }),
        columnHelper.accessor('title', {
            header: 'Title',
        }),
        columnHelper.accessor('userName', {
            header: 'User Name',
            cell: info => (
                <div className="line-clamp-1">
                    {info.getValue() || 'N/A'}
                </div>
            ),
        }),
        {
            accessorKey: 'createdAt',
            header: 'Created Date',
            meta: {
                filterVariant: 'date',
            },
            cell: ({ getValue }) => {
                const value = getValue();
                return value ? new Date(value).toLocaleDateString() : '';
            }
        },
        columnHelper.accessor('active', {
            header: 'Active',
            align: 'center',
            cell: info => (
                <div className="">
                    <Chip value={info.getValue() ? "Yes" : "No"} color={info.getValue() ? 'green' : 'inactive'} />
                </div>
            ),
        }),
        columnHelper.accessor('showInHomePage', {
            header: 'Show in Homepage',
            align: 'center',
            cell: info => (
                <div className="">
                    <Chip value={info.getValue() ? "Yes" : "No"} color={info.getValue() ? 'green' : 'inactive'} />
                </div>
            ),
        }),
        columnHelper.accessor('like', {
            header: 'Likes',
            align: 'center',
            cell: info => (
                <div className="">
                    {info.getValue().length || 0}
                </div>
            ),
        }),
        columnHelper.accessor('comment', {
            header: 'Comments',
            align: 'center',
            cell: info => (
                <div className="">
                    {info.getValue().length || 0}
                </div>
            ),
        }),
        columnHelper.accessor('actions', {
            header: 'Actions',
            enableColumnFilter: false,
            cell: info => {
                return (
                    <div className='flex items-center justify-center'>
                        <button
                            className="rounded p-1.5 bg-[rgba(52,199,89,0.1)] hover:bg-[rgba(52,199,89,0.2)] mx-1 min-h-7 min-w-7"
                            onClick={() => navigate('/eco-activity/edit-eco-activity-details', { state: { id: info.row.original?._id } })}
                        >
                            <img src={EditIcon} alt="view" />
                        </button>
                        <button
                            className="rounded p-1.5 bg-[rgba(199,52,52,0.1)] hover:bg-[rgba(199,52,52,0.2)] mx-1 min-h-7"
                            onClick={() => handleDelete(info.row.original?._id)}
                            disabled={isDeleting}
                        >
                            <FaTrash className="fill-chipRed" />
                        </button>
                    </div>
                )
            },
        }),
    ], [navigate]);

    if (isPending || isUserPending) {
        return (
            <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
                <section className="flex flex-col pt-5 w-full bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                    <div className="flex flex-col pr-px pl-6 w-full max-md:pl-5 max-md:max-w-full">
                        <h1 className="self-start text-lg font-semibold">List of Eco Activities</h1>
                        <p>Loading...</p>
                    </div>
                </section>
            </main>
        );
    }

    return (
        <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
            {/* Filters */}
            <form
                className="flex flex-col pt-5 w-full bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full"
                onSubmit={handleSearch}
            >
                <div className='flex items-center justify-between pr-5'>
                    <div className="flex gap-1.5 items-start my-auto text-base whitespace-nowrap ml-5">
                        <img
                            loading="lazy"
                            src="https://cdn.builder.io/api/v1/image/assets/TEMP/4dd39feef0ff4934d9e2e86b8c7285d5d7532aeb26adcdc1e878d021439e2681?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
                            alt=""
                            className="object-contain shrink-0 w-5 aspect-square"
                        />
                        <span className="self-stretch">Filters</span>
                        <img
                            loading="lazy"
                            src="https://cdn.builder.io/api/v1/image/assets/TEMP/d18f51a0b2d597a302f55b77fc1071043eab5f274a1cc9e9b3fe3e2c97520a8c?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
                            alt=""
                            className="object-contain shrink-0 aspect-square w-[18px]"
                        />
                    </div>
                </div>
                <div className="flex flex-wrap gap-4 p-5">
                    <div className='flex gap-3 w-full'>
                        <div className="flex-col w-full">
                            <label htmlFor="userId" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                User
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <select
                                    name="userId"
                                    value={filters.userId}
                                    onChange={handleFilterChange}
                                    className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                                >
                                    <option value="">All Users</option>
                                    {Array.isArray(users) && users.map((user) => (
                                        <option key={user._id} value={user._id}>
                                            {user.username}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="flex-col w-full">
                            <label htmlFor="title" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                Activity Title
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="text"
                                    name="title"
                                    value={filters.title}
                                    onChange={handleFilterChange}
                                    placeholder="Enter Activity Title"
                                    className='outline-none focus:ring-0 w-full'
                                />
                            </div>
                        </div>
                    </div>
                    <div className='flex gap-3 w-full'>
                        <div className="flex-col w-full">
                            <label htmlFor="status" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                Status
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <select
                                    name="status"
                                    value={filters.status}
                                    onChange={handleFilterChange}
                                    className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                                >
                                    <option value="">All Status</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex-col w-full">
                            <label htmlFor="homepage" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                Homepage
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <select
                                    name="homepage"
                                    value={filters.homepage}
                                    onChange={handleFilterChange}
                                    className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                                >
                                    <option value="">All Homepage</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex-col w-full">
                            {/* Empty column for spacing */}
                        </div>
                    </div>
                </div>
                <div className='flex gap-3 items-center justify-end p-5'>
                    <ActionButton
                        variant={'reset'}
                        text={'Reset'}
                        handleSubmit={() => {
                            setFilters({
                                title: '',
                                userId: '',
                                status: '',
                                homepage: '',
                            });
                            setFilteredData(data);
                        }}
                    />
                    <ActionButton
                        variant={'submit'}
                        text={'Search'}
                        handleSubmit={handleSearch}
                    />
                </div>
            </form>

            <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                <div className="flex flex-col pr-px w-full max-md:max-w-full">
                    <h1 className="self-start text-lg font-bold pl-6 pb-4">List of Eco Activities</h1>
                    <div className="pl-5">
                        <TanStackTable
                            data={Array.isArray(filteredData) ? filteredData : (Array.isArray(data) ? data : [])}
                            columns={columns}
                            enablePagination={true}
                        />
                    </div>
                </div>
            </section>
            <ToastContainer
                position="top-right"
                autoClose={3000}
            />
        </main>
    )
}

export default EcoActivityList;